import React, {
  createContext,
  Fragment,
  ReactNode,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';

export type RightDrawerContextType = {
  visibility: boolean;
  component: ReactNode | null;
  openDrawerWithComponent: (component: ReactNode) => void;
  closeDrawer: () => void;
};

const initialContext: RightDrawerContextType = {
  visibility: false,
  component: null,
  openDrawerWithComponent: () => {
    // do nothing
  },
  closeDrawer: () => {
    // do nothing
  },
};

const RightDrawerContext = createContext(initialContext);

export function RightDrawerProvider({ children }: { children: ReactNode }) {
  const [visibility, setVisibility] = useState<boolean>(false);
  const [component, setComponent] = useState<ReactNode | null>(null);

  function openDrawerWithComponent(component: ReactNode) {
    setComponent(component);
    setVisibility(true);
  }

  function closeDrawer() {
    setVisibility(false);
  }

  return (
    <RightDrawerContext.Provider
      value={{
        visibility,
        openDrawerWithComponent,
        closeDrawer,
        component,
      }}
    >
      {children}
    </RightDrawerContext.Provider>
  );
}

export const useRightDrawer = () => useContext(RightDrawerContext);

export function RightDrawer() {
  const { visibility, closeDrawer, component } = useContext(RightDrawerContext);

  // Resize functionality
  const [width, setWidth] = useState(400); // Default width
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isResizingRef = useRef(false);

  const MIN_WIDTH = 300;
  const MAX_WIDTH = 800;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      isResizingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width],
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;

    const deltaX = startXRef.current - e.clientX; // Negative because we're dragging left
    const newWidth = Math.max(
      MIN_WIDTH,
      Math.min(MAX_WIDTH, startWidthRef.current + deltaX),
    );
    setWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback((e?: MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsResizing(false);
    isResizingRef.current = false;
  }, []);

  const handleMouseUpOnHandle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(false);
    isResizingRef.current = false;
  }, []);

  // Add event listeners for mouse move and up
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const content = visibility && component ? component : null;

  return (
    <Transition show={visibility} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40"
        onClose={isResizing ? () => {} : closeDrawer}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity z-40" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex pl-10">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="flex pointer-events-auto">
                  {/* Resize handle */}
                  <div
                    className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors duration-200 flex-shrink-0 hover:w-1.5"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUpOnHandle}
                  />
                  <DialogPanel
                    className="flex flex-col shadow-xl bg-white h-screen overflow-y-scroll"
                    style={{ width: `${width}px` }}
                  >
                    {content}
                  </DialogPanel>
                </div>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
